import { Link } from 'react-router-dom';
import { useOnboarding } from '../contexts/OnboardingContext';
import { CheckCircle, Plus, ArrowRight, X } from 'lucide-react';

const STEP_CONFIG = {
  wallet_funded: {
    label: 'Top up your wallet',
    description: 'Add SMS credits to start sending',
    completedDescription: 'Wallet funded',
    href: '/dashboard/wallet',
  },
  customers_added: {
    label: 'Import customers',
    description: 'Add customers or connect ordering system',
    completedDescription: 'Customers added',
    href: '/dashboard/customers',
  },
  template_created: {
    label: 'Create a template',
    description: 'Save reusable SMS templates — try our AI writer!',
    completedDescription: 'Template created',
    href: '/dashboard/templates',
  },
  campaign_sent: {
    label: 'Send your first campaign',
    description: 'Send an SMS campaign to your customers',
    completedDescription: 'Campaign sent',
    href: '/dashboard/campaigns/new',
  },
};

export default function GettingStartedChecklist() {
  const { completedSteps, checklistDismissed, allStepsComplete, dismissChecklist, steps, loaded } = useOnboarding();

  if (!loaded || checklistDismissed) return null;

  const doneCount = steps.filter(s => completedSteps.includes(s)).length;

  // All steps complete — show congratulations card
  if (allStepsComplete) {
    return (
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-sm p-4 lg:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-white" />
            <div>
              <h2 className="text-lg font-semibold text-white">All set!</h2>
              <p className="text-sm text-green-100">You've completed all getting started steps</p>
            </div>
          </div>
          <button
            onClick={dismissChecklist}
            className="p-1.5 text-green-200 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Dismiss checklist"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="w-full h-2 bg-white/20 rounded-full mt-3 overflow-hidden">
          <div className="h-full bg-white rounded-full w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Getting Started</h2>
          <p className="text-sm text-gray-500 mt-0.5">{doneCount} of {steps.length} complete</p>
        </div>
        <button
          onClick={dismissChecklist}
          className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Dismiss checklist"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-100 rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-orange-500 rounded-full transition-all duration-500"
          style={{ width: `${(doneCount / steps.length) * 100}%` }}
        />
      </div>

      <div className="space-y-3">
        {steps.map((stepId) => {
          const config = STEP_CONFIG[stepId];
          const done = completedSteps.includes(stepId);
          return (
            <Link
              key={stepId}
              to={config.href}
              className={`flex items-center gap-4 p-3 rounded-lg transition-all ${
                done
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-gray-50 border border-gray-200 hover:border-orange-200 hover:bg-orange-50'
              }`}
              data-testid={`checklist-${stepId}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                done ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                {done ? (
                  <CheckCircle className="w-5 h-5 text-white" />
                ) : (
                  <Plus className="w-5 h-5 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${done ? 'text-green-800' : 'text-gray-900'}`}>
                  {config.label}
                </p>
                <p className={`text-sm ${done ? 'text-green-600' : 'text-gray-500'}`}>
                  {done ? config.completedDescription : config.description}
                </p>
              </div>
              <ArrowRight className={`w-4 h-4 flex-shrink-0 ${done ? 'text-green-500' : 'text-gray-400'}`} />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
